import { Box, GridItem, useMediaQueryContext } from "@drone-js/core";
import { useDisclosure } from "apps/commons/hooks/useDisclosure";
import type { OneOnboardingPhoto, PropertyData } from "libs/codegen/property-onboarding";
import type { FunctionComponent } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { pictureTypeMapper } from "../constants";
import { useHover } from "../useHover";
import { MainPhotoBadge } from "./MainPhotoBadge";
import { Menu } from "./Menu";
import { MobileMenu } from "./MobileMenu";
import { PropertyImage } from "./PropertyImage";
import { RemovePhotoModal } from "./RemovePhotoModal";

interface Props {
    targetField: string;
    index: number;
    onRemove: () => void;
    onMain: () => void;
    disableMainPhoto?: boolean;
}

export const PhotoController: FunctionComponent<Props> = ({
    targetField,
    index,
    onMain,
    onRemove,
    disableMainPhoto,
}) => {
    const [isHover, bindHover] = useHover();
    const { open: removeModal, onClose: closeRemoveModal, onOpen: openRemoveModal } = useDisclosure();
    const { open: mobileSheet, onClose: closeMobileSheet, onOpen: openMobileSheet } = useDisclosure();
    const context = useMediaQueryContext();

    const photo = useWatch<PropertyData>({
        name: `${targetField}.${index}` as "photos.0",
    }) as OneOnboardingPhoto;
    const { setValue, getValues } = useFormContext<PropertyData>();

    const handleSetMainPhoto = () => {
        // current main photo back to property
        if (getValues(`${targetField}.0.pictureTypeId` as "photos.0.pictureTypeId") === pictureTypeMapper.mainPhoto)
            setValue(`${targetField}.0.pictureTypeId` as "photos.0.pictureTypeId", pictureTypeMapper.property);
        // set current photo to main
        setValue(`${targetField}.${index}.pictureTypeId` as "photos.0.pictureTypeId", pictureTypeMapper.mainPhoto);
        // call onMain on parent to swap order
        onMain();
    };

    const isMainPhoto = photo.pictureTypeId === pictureTypeMapper.mainPhoto;

    return (
        <GridItem sm={6} md={4}>
            <Box
                position="relative"
                {...(context.m
                    ? bindHover
                    : {
                          onClick: openMobileSheet,
                      })}
            >
                {isMainPhoto && <MainPhotoBadge />}
                {context.m ? (
                    <Menu
                        isMainPhoto={isMainPhoto}
                        isHover={isHover}
                        onMain={handleSetMainPhoto}
                        onRemove={openRemoveModal}
                        hideMainPhoto={disableMainPhoto}
                    />
                ) : (
                    <MobileMenu
                        src={photo.src}
                        isMainPhoto={isMainPhoto}
                        show={mobileSheet}
                        onClose={closeMobileSheet}
                        onMain={handleSetMainPhoto}
                        onRemove={openRemoveModal}
                        hideMainPhoto={disableMainPhoto}
                    />
                )}
                <PropertyImage src={photo.src} />
            </Box>
            <RemovePhotoModal photoId={photo.id} show={removeModal} onCancel={closeRemoveModal} onRemove={onRemove} />
        </GridItem>
    );
};
